/** @file Dopesheet rows: one summary row per target, its channel rows unless collapsed, sorted key frames. */
import { invariant } from '@pose2d/std/invariant';

/** One keyed channel of a target (e.g. bone rotation) with its key frames in any order. */
export interface TimelineChannelInput {
  channel: string;
  label: string;
  frames: readonly number[];
}

/** One target (bone, slot, IK constraint, deform attachment, draw order, events) and its channels. */
export interface TimelineGroupInput {
  target: string;
  label: string;
  channels: readonly TimelineChannelInput[];
}

/** A laid-out row; y is its top edge in content pixels (row index x rowHeight). */
export interface TimelineRow {
  kind: 'group' | 'channel';
  target: string;
  /** Channel key for channel rows; null on group rows. */
  channel: string | null;
  label: string;
  y: number;
  /** Ascending unique key frames; a group row holds the union of all its channels. */
  frames: Float64Array;
  /** Group rows: whether the channel rows are hidden; always false on channel rows. */
  collapsed: boolean;
  /** Group rows: every channel key of the target (also when collapsed); empty on channel rows. */
  channels: string[];
}

/** Rows in display order plus the uniform row height and total content height. */
export interface TimelineLayout {
  rows: TimelineRow[];
  rowHeight: number;
  height: number;
}

/** Ascending unique union of frame lists. */
function sortedUnique(lists: readonly (readonly number[])[]): Float64Array {
  const seen = new Set<number>();
  for (const list of lists) for (const f of list) seen.add(f);
  return Float64Array.from(seen).sort();
}

/** Lays out the dopesheet; groups whose target is in `collapsed` show only their summary row. */
export function timelineLayout(groups: readonly TimelineGroupInput[], collapsed: ReadonlySet<string>, rowHeight: number): TimelineLayout {
  invariant(rowHeight > 0, 'timelineLayout: rowHeight must be > 0');
  const rows: TimelineRow[] = [];
  const push = (row: Omit<TimelineRow, 'y'>): void => {
    rows.push({ ...row, y: rows.length * rowHeight });
  };
  for (const g of groups) {
    const hidden = collapsed.has(g.target);
    const channels = g.channels.map((c) => c.channel);
    push({ kind: 'group', target: g.target, channel: null, label: g.label, frames: sortedUnique(g.channels.map((c) => c.frames)), collapsed: hidden, channels });
    if (hidden) continue;
    for (const c of g.channels) {
      push({ kind: 'channel', target: g.target, channel: c.channel, label: c.label, frames: sortedUnique([c.frames]), collapsed: false, channels: [] });
    }
  }
  return { rows, rowHeight, height: rows.length * rowHeight };
}
